import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { AddressAutocompletePrecisely } from "@covertree/features";
import { useFormWithNotification } from "@covertree/hooks";
import { Toast } from "@covertree/utils";
import { Address } from "@covertree/new-business-graphql";
import { Button, Form, FormTextField, Modal } from "@covertree/ui";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, DialogActions, Grid } from "@mui/material";

import { MailingAddressForm, mailingAddressFormValidator } from "./form";

interface Props {
  mailingAddress: Address;
  onClose(): void;
  onSubmit(data: MailingAddressForm): Promise<void>;
  open: boolean;
}

export function UpdateMailingAddressModal({
  mailingAddress,
  onSubmit,
  open,
  onClose,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const defaultAddress = useMemo((): MailingAddressForm => {
    const [addressNumber, ...streetName] =
      mailingAddress.streetAddress.split(" ");

    const lotUnit = mailingAddress.unitNumber?.trim();

    const formattedAddress = `${mailingAddress.streetAddress}${lotUnit ? `, ${lotUnit},` : ""} ${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.zipCode}`;

    return {
      address: {
        label: formattedAddress,
        value: {
          addressLastLine: `${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.zipCode}`,
          addressNumber,
          city: mailingAddress.city,
          country: mailingAddress.countryCode,
          formattedAddress,
          mainAddressLine: mailingAddress.streetAddress,
          postCode: mailingAddress.zipCode,
          state: mailingAddress.state,
          streetName: streetName.join(" "),
          lotUnit,
        },
      },
      lotUnit,
    };
  }, [mailingAddress]);

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: defaultAddress,
    delayError: FORM_ERROR_DELAY_IN_MS,
    resolver: yupResolver(mailingAddressFormValidator),
  });

  const {
    control,
    handleSubmit: submit,
    reset,
    formState: { isDirty },
    setValue,
    watch,
  } = form;

  const handleSubmit = useCallback(
    async ({ address, lotUnit }: MailingAddressForm) => {
      try {
        if (!isDirty) {
          onClose();
          return;
        }
        setIsLoading(true);

        await onSubmit({
          address,
          lotUnit: lotUnit?.trim(),
        });

        onClose();
      } catch {
        Toast.showError("Could not update mailing address");
      } finally {
        setIsLoading(false);
      }
    },
    [isDirty, onClose]
  );

  const onCancel = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const address = watch("address");

  useEffect(() => {
    if (address?.value) {
      setValue("lotUnit", address.value.lotUnit ?? "");
    }
  }, [address, setValue]);

  useEffect(() => {
    reset(defaultAddress);
  }, [defaultAddress]);

  return (
    <Modal
      open={open}
      title="Edit mailing address"
      onClose={onClose}
      disableClose={isLoading}
      maxWidth="md">
      <Form onSubmit={submit(handleSubmit)}>
        <FormProvider {...form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AddressAutocompletePrecisely
                id="mailing-address"
                name="address"
                label="Address"
                control={control}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                id="mailing-unit"
                name="lotUnit"
                label="Unit / Lot#"
                control={control}
                disabled={isLoading}
                optional
                tooltip="The autocomplete should have all of the lot or unit options you need, but if it doesn't, please use this box to fill in the correct lot or unit information for the address."
                helperText="Note: Unit information entered here will override the unit selected above, if any."
              />
            </Grid>
          </Grid>
        </FormProvider>
        <Box mt={5} mb={2}>
          <DialogActions>
            <Button
              type="submit"
              data-testid="submit-edit-mailing-form"
              loading={isLoading}
              disabled={isLoading}>
              Submit
            </Button>
            <Button
              disabled={isLoading}
              variant="outlined"
              onClick={onCancel}
              data-testid="cancel-edit-mailing-form">
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </Form>
    </Modal>
  );
}
